import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, timer } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";
import { environment } from "src/environments/environment";

const BASE = `${environment.apiUrl}/property-extraction`;

export type ExtractionRunStatus =
  | "TRIAGE"
  | "QUOTED"
  | "PROCESSING"
  | "REVIEW"
  | "APPLIED"
  | "CANCELLED"
  | "FAILED"
  | "EXPIRED";

export type ExtractionDocumentStatus =
  | "PENDING"
  | "TRIAGED"
  | "PROCESSING"
  | "DONE"
  | "FAILED"
  | "UNREADABLE"
  | "SKIPPED";

export type ExtractionDecision = "PENDING" | "ACCEPTED" | "EDITED" | "REJECTED";
export type ExtractionBucket = "AUTO" | "REVIEW" | "SUGGESTION";
export type ExtractionItemKind =
  | "FIELD"
  | "OWNER"
  | "DIVISION"
  | "CHARACTERISTIC"
  | "PROXIMITY"
  | "ENCUMBRANCE"
  | "EXTRA";

export interface ExtractionPricing {
  printedDocument: number;
  handwrittenDocument: number;
  imageBatch: number;
  imageBatchSize: number;
  pagesIncluded: number;
  extraPagesBlock: number;
  extraPagesBlockCost: number;
}

export interface ExtractionPublicConfig {
  pricing: ExtractionPricing;
  limits: {
    maxDocuments: number;
    maxImages: number;
    maxFileMb: number;
    autoAcceptThreshold: number;
    reviewThreshold: number;
  };
}

export interface ExtractionQuoteSummary {
  printed: { count: number; credits: number };
  handwritten: { count: number; credits: number };
  images: { count: number; batches: number; credits: number };
  extraPages: { pages: number; credits: number };
}

export interface ExtractionDocument {
  id: number;
  fileName: string;
  mimeType: string;
  size: number;
  pageCount: number;
  status: ExtractionDocumentStatus;
  detectedType: string | null;
  typeConfidence: number | null;
  isHandwritten: boolean;
  readable: boolean;
  billingCategory: "PRINTED" | "HANDWRITTEN" | "IMAGE";
  quotedCredits: number;
  billedCredits: number;
  propertyDocumentTypeId: number | null;
  propertyDocumentTypeName: string | null;
  attachToProperty: boolean;
  fromProperty: boolean;
  summary: string | null;
  warnings: string[];
  errorCode: string | null;
  errorMessage: string | null;
}

export interface ExtractionRun {
  id: number;
  status: ExtractionRunStatus;
  source: string;
  propertyId: string | null;
  quotedCredits: number;
  reservedCredits: number;
  chargedCredits: number;
  overallConfidence: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  progress: { total: number; done: number };
  quote: ExtractionQuoteSummary;
  pricing: ExtractionPricing;
  thresholds: { autoAccept: number; review: number };
  summary: any;
  documents: ExtractionDocument[];
  rejected?: Array<{ fileName: string; reason: string }>;
}

export interface ExtractionItem {
  id: number;
  kind: ExtractionItemKind;
  key: string;
  group: string | null;
  documentId: number | null;
  value: string | null;
  numericValue: number | null;
  dateValue: string | null;
  unit: string | null;
  catalogId: number | null;
  confidence: number | null;
  page: number | null;
  snippet: string | null;
  handwritten: boolean;
  bucket: ExtractionBucket;
  decision: ExtractionDecision;
  finalValue: any;
  decidedByUser: boolean;
  applied: boolean;
  payload: any;
  alternatives?: ExtractionItem[];
}

export interface ExtractionResult {
  run: ExtractionRun;
  items: ExtractionItem[];
}

export interface ExtractionProvenanceField {
  kind: ExtractionItemKind;
  key: string;
  runId: number;
  value: any;
  displayValue: string | null;
  confidence: number | null;
  page: number | null;
  form: { tab: string; control: string } | null;
  documentName: string | null;
  documentType: string | null;
  appliedAt: string;
}

const OPTIONS = { withCredentials: true };

/** API da extração de documentos com IA. */
@Injectable({ providedIn: "root" })
export class PropertyExtractionService {
  constructor(private http: HttpClient) {}

  getPricing(): Observable<ExtractionPublicConfig> {
    return this.http.get<ExtractionPublicConfig>(`${BASE}/pricing`, OPTIONS);
  }

  createRun(params: {
    files?: File[];
    propertyFileIds?: number[];
    propertyId?: string | null;
    source?: "WIZARD" | "DOCUMENTS_TAB";
  }): Observable<ExtractionRun> {
    return this.http.post<ExtractionRun>(
      `${BASE}/runs`,
      this.toFormData(params),
      OPTIONS,
    );
  }

  addDocuments(
    runId: number,
    params: { files?: File[]; propertyFileIds?: number[] },
  ): Observable<ExtractionRun> {
    return this.http.post<ExtractionRun>(
      `${BASE}/runs/${runId}/documents`,
      this.toFormData(params),
      OPTIONS,
    );
  }

  removeDocument(runId: number, documentId: number): Observable<ExtractionRun> {
    return this.http.delete<ExtractionRun>(
      `${BASE}/runs/${runId}/documents/${documentId}`,
      OPTIONS,
    );
  }

  retryDocument(runId: number, documentId: number): Observable<ExtractionRun> {
    return this.http.post<ExtractionRun>(
      `${BASE}/runs/${runId}/documents/${documentId}/retry`,
      {},
      OPTIONS,
    );
  }

  getRun(runId: number): Observable<ExtractionRun> {
    return this.http.get<ExtractionRun>(`${BASE}/runs/${runId}`, OPTIONS);
  }

  findOpenRun(propertyId?: string | null): Observable<ExtractionRun | null> {
    let params = new HttpParams();
    if (propertyId) params = params.set("propertyId", propertyId);
    return this.http.get<ExtractionRun | null>(`${BASE}/runs/open`, {
      ...OPTIONS,
      params,
    });
  }

  confirm(runId: number): Observable<ExtractionRun> {
    return this.http.post<ExtractionRun>(
      `${BASE}/runs/${runId}/confirm`,
      {},
      OPTIONS,
    );
  }

  cancel(runId: number): Observable<ExtractionRun> {
    return this.http.post<ExtractionRun>(
      `${BASE}/runs/${runId}/cancel`,
      {},
      OPTIONS,
    );
  }

  getResult(runId: number): Observable<ExtractionResult> {
    return this.http.get<ExtractionResult>(
      `${BASE}/runs/${runId}/result`,
      OPTIONS,
    );
  }

  getDocumentFile(runId: number, documentId: number): Observable<ArrayBuffer> {
    return this.http.get(`${BASE}/runs/${runId}/documents/${documentId}/file`, {
      ...OPTIONS,
      responseType: "arraybuffer",
    });
  }

  saveDecisions(
    runId: number,
    body: {
      fields?: Array<{ id: number; decision: ExtractionDecision; finalValue?: any }>;
      documents?: Array<{
        id: number;
        propertyDocumentTypeId?: number | null;
        attachToProperty?: boolean;
      }>;
    },
  ): Observable<{ ok: boolean }> {
    return this.http.patch<{ ok: boolean }>(
      `${BASE}/runs/${runId}/decisions`,
      body,
      OPTIONS,
    );
  }

  apply(
    runId: number,
    body: { propertyId: string; appliedFieldKeys?: string[] },
  ): Observable<{
    ok: boolean;
    alreadyApplied: boolean;
    attached: number;
    warnings: string[];
  }> {
    return this.http.post<any>(`${BASE}/runs/${runId}/apply`, body, OPTIONS);
  }

  getProvenance(
    propertyId: string,
  ): Observable<{ propertyId: string; fields: ExtractionProvenanceField[] }> {
    return this.http.get<any>(`${BASE}/provenance/${propertyId}`, OPTIONS);
  }

  /**
   * Acompanha o run até sair de um dos estados indicados. Emite uma vez, com o
   * run já no estado seguinte. Cada pedido só parte depois de o anterior
   * responder.
   */
  waitWhile(
    runId: number,
    statuses: ExtractionRunStatus[],
    options: { intervalMs?: number; onTick?: (run: ExtractionRun) => void } = {},
  ): Observable<ExtractionRun> {
    const intervalMs = options.intervalMs ?? 2500;
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getRun(runId)),
      filter((run) => {
        options.onTick?.(run);
        return statuses.indexOf(run.status) < 0;
      }),
      take(1),
    );
  }

  private toFormData(params: {
    files?: File[];
    propertyFileIds?: number[];
    propertyId?: string | null;
    source?: string;
  }): FormData {
    const formData = new FormData();
    if (params.propertyId) formData.append("propertyId", params.propertyId);
    if (params.source) formData.append("source", params.source);
    if (params.propertyFileIds?.length) {
      formData.append("propertyFileIds", JSON.stringify(params.propertyFileIds));
    }
    for (const file of params.files ?? []) {
      formData.append("files", file, file.name);
    }
    return formData;
  }
}
